/**
 * Voice Recording Service
 * Handles microphone recording and transcription via the /stt endpoint
 */

import { STT_WORKER_URL } from '../config.js';

class VoiceRecordingService {
    constructor() {
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.isRecording = false;
        this.stream = null;
    }

    /**
     * Check if browser supports audio recording
     */
    isSupported() {
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    /**
     * Start recording audio from microphone
     */
    async startRecording() {
        if (!this.isSupported()) {
            throw new Error('Audio recording is not supported in this browser');
        }

        if (this.isRecording) {
            console.warn('Already recording');
            return;
        }

        try {
            // Request microphone access
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Create MediaRecorder with appropriate MIME type
            const mimeType = this.getSupportedMimeType();
            this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
            
            this.audioChunks = [];
            
            this.mediaRecorder.addEventListener('dataavailable', (event) => {
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            });

            this.mediaRecorder.start();
            this.isRecording = true;
            
            console.log('Voice recording started');
        } catch (error) {
            console.error('Failed to start recording:', error);
            this.cleanup();
            throw error;
        }
    }

    /**
     * Stop recording and return the audio blob
     */
    async stopRecording() {
        if (!this.isRecording || !this.mediaRecorder) {
            throw new Error('No active recording');
        }

        return new Promise((resolve, reject) => {
            this.mediaRecorder.addEventListener('stop', () => {
                try {
                    const audioBlob = new Blob(this.audioChunks, { type: this.getSupportedMimeType() });
                    this.cleanup();
                    resolve(audioBlob);
                } catch (error) {
                    reject(error);
                }
            });

            this.mediaRecorder.stop();
            this.isRecording = false;
        });
    }

    /**
     * Cancel recording without returning audio
     */
    cancelRecording() {
        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop();
            this.isRecording = false;
        }
        this.cleanup();
    }

    /**
     * Clean up resources
     */
    cleanup() {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.isRecording = false;
    }

    /**
     * Get supported MIME type for recording
     */
    getSupportedMimeType() {
        const types = [
            'audio/webm',
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/mp4',
            'audio/wav'
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        return 'audio/webm'; // Fallback
    }

    /**
     * Convert audio blob to format suitable for Whisper API
     */
    async convertAudioForWhisper(audioBlob) {
        // Convert blob to array buffer
        const arrayBuffer = await audioBlob.arrayBuffer();
        
        // Convert to Uint8Array (array of integers 0-255)
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // Convert to regular array for JSON serialization
        return Array.from(uint8Array);
    }

    /**
     * Send audio to transcription endpoint
     */
    async transcribeAudio(audioBlob) {
        try {
            const audioArray = await this.convertAudioForWhisper(audioBlob);
            
            // Get auth token from sessionStorage
            const token = await window.tokenManager?.getValidAccessToken?.() ?? null;

            if (!token) {
                throw new Error('Authentication required for voice transcription');
            }

            const workerUrl = `${STT_WORKER_URL}/stt`;

            const response = await fetch(workerUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    audio: audioArray
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Transcription failed: ${response.status}`);
            }

            const result = await response.json();
            console.log('Transcription result:', result);
            console.log('Transcribed text:', result.text);
            console.log('Word count:', result.word_count);
            
            return result.text || '';
        } catch (error) {
            console.error('Transcription error:', error);
            throw error;
        }
    }

    /**
     * Get current recording state
     */
    getRecordingState() {
        return {
            isRecording: this.isRecording,
            isSupported: this.isSupported()
        };
    }
}

// Export singleton instance
export const voiceRecordingService = new VoiceRecordingService();

